import { useEffect, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { cn } from '../../../lib/utils';
import { BoldIcon, ItalicIcon, LinkIcon, ListIcon, ListOrderedIcon } from '../../../icons';

export interface RichTextEditorProps {
  /** Current value as an HTML string. Empty string when the editor is empty. */
  value: string;
  /** Emits the HTML string, or `''` when the editor is empty (so callers can
   *  keep mapping empty → `null` exactly like a `<Textarea>`). */
  onChange: (html: string) => void;
  /** Fired when the editable surface loses focus (mirrors `<Textarea onBlur>`). */
  onBlur?: () => void;
  disabled?: boolean;
  /** Inline error styling — border + ring turn destructive. */
  hasError?: boolean;
  className?: string;
  'data-testid'?: string;
}

// The editable surface uses the same `prose` shape the candidate app renders the
// HTML with, so what the recruiter types matches what the candidate sees.
const PROSE_CLASS =
  'prose prose-sm max-w-none min-h-24 px-3 py-2 text-sm leading-6 text-foreground focus:outline-none ' +
  '[&_a]:text-primary [&_a]:underline [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5';

function ToolbarButton({
  active = false,
  disabled = false,
  label,
  testId,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  label: string;
  testId?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      data-testid={testId}
      // Keep the editor selection while clicking the toolbar.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
        active && 'bg-accent text-accent-foreground'
      )}
    >
      {children}
    </button>
  );
}

/**
 * RichTextEditor — a Tiptap-backed WYSIWYG whose VALUE is an HTML string. The
 * recruiter authors descriptions / FAQ answers / step content here; the
 * candidate app renders the same HTML (sanitized) via `dangerouslySetInnerHTML`.
 * Mirrors the look of `Textarea` (bordered surface + focus ring) and emits `''`
 * when empty so empty → `null` mapping keeps working.
 */
export function RichTextEditor({
  value,
  onChange,
  onBlur,
  disabled = false,
  hasError = false,
  className,
  'data-testid': testId,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      // StarterKit v3 ships a Link extension; disable it so our configured one
      // is the single `link` mark (avoids a duplicate-extension error).
      StarterKit.configure({ link: false, heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || '',
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
    onBlur: () => onBlur?.(),
    editorProps: {
      attributes: {
        class: PROSE_CLASS,
        ...(testId ? { 'data-testid': `${testId}-content` } : {}),
      },
    },
  });

  // Hydrate the editor when `value` changes from the outside (form reset / async
  // load), without emitting an update and without clobbering the user mid-type.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if ((value || '') !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const promptLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL do link', previous ?? 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div
      data-slot="rich-text-editor"
      data-testid={testId}
      className={cn(
        'flex flex-col rounded-md border bg-background shadow-2xs transition-colors',
        hasError
          ? 'border-destructive focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-destructive'
          : 'border-input focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-primary',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <ToolbarButton
          label="Negrito"
          testId={testId ? `${testId}-bold` : undefined}
          active={editor?.isActive('bold') ?? false}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <BoldIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Itálico"
          testId={testId ? `${testId}-italic` : undefined}
          active={editor?.isActive('italic') ?? false}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Lista com marcadores"
          testId={testId ? `${testId}-bullet-list` : undefined}
          active={editor?.isActive('bulletList') ?? false}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <ListIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Lista numerada"
          testId={testId ? `${testId}-ordered-list` : undefined}
          active={editor?.isActive('orderedList') ?? false}
          disabled={disabled || !editor}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrderedIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Link"
          testId={testId ? `${testId}-link` : undefined}
          active={editor?.isActive('link') ?? false}
          disabled={disabled || !editor}
          onClick={promptLink}
        >
          <LinkIcon className="size-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
